<script setup lang="ts">
  import { format } from 'date-fns'
  import { computed } from 'vue'
  import StarRatingDisplay from '~renderer/components/StarRatingDisplay.vue'
  import { useIslandTime } from '~renderer/composables/useIslandTime'
  import { typeColors } from '~renderer/util/island'
  import { onGetUserVisitHistory } from '~rpc/procedures/user/onGetUserVisitHistory.telefunc'
  import type { InferTelefuncReturn } from '~shared/util/type'

  type UserVisitRecord = InferTelefuncReturn<typeof onGetUserVisitHistory>[number]

  const props = defineProps<{
    visit: UserVisitRecord
  }>()

  const emit = defineEmits<{
    report: [visitId: string]
  }>()

  const { formatDuration } = useIslandTime()

  const visitTime = computed(() => format(props.visit.visitedAt, 'M/d/yyyy h:mm a'))
  const fruitName = computed(() => titleTextOf(props.visit?.islandSnapshot?.fruit || ''))
  const npcIcons = computed(() => (props.visit?.islandSnapshot?.npc || []).map(iconOf))

  const iconOf = (
    item: NonNullable<NonNullable<typeof props.visit.islandSnapshot>['npc']>[number],
  ): { path: string; alt: string } => {
    const npcImageName = item.replace(/ /g, '-').replaceAll('.', '').toLowerCase()

    return { path: `/images/npc/${npcImageName}.svg`, alt: item }
  }

  const titleTextOf = (str: string): string =>
    `${str.slice(0, 1).toUpperCase()}${str.slice(1).toLowerCase()}`

  const onReportIslandClicked = async (visitId: string) => emit('report', visitId)
</script>

<template>
  <div
    v-if="visit && visit.islandSnapshot"
    class="group border-base-tan relative flex flex-col rounded-2xl border bg-[#2d262f]"
    data-prefetch-static-assets="false"
  >
    <div
      class="bg-tan-800 grid w-full grid-cols-[1.5rem_1fr_4rem] gap-2 rounded-t-2xl px-3 py-2 text-white"
    >
      <i-fa7-solid-plane class="size-6 -rotate-45" />
      <div class="truncate font-medium">
        <span>{{ visit.islandSnapshot.name }}</span>
        <button
          class="ml-2 cursor-pointer text-white/40 hover:text-white"
          @click="onReportIslandClicked(visit.visitId)"
        >
          <i-fa7-solid-flag class="size-4" />
        </button>
      </div>
      <div class="flex flex-row items-center justify-end">
        <StarRatingDisplay :rating="visit.rating || 0" :size="5" :padding="0" />
      </div>
    </div>
    <div class="grid w-full grid-cols-[1fr_7rem] gap-2 px-3 py-2 text-white">
      <div class="flex flex-row items-center gap-3">
        <div
          class="truncate rounded-md px-1.5 py-0.5 text-xs font-semibold"
          :class="typeColors[visit.islandSnapshot.type]"
        >
          {{ visit.islandSnapshot.type.toUpperCase() }}
        </div>
        <div
          class="truncate rounded-md px-1.5 py-0.5 text-xs font-semibold"
          :class="typeColors[visit.islandSnapshot.type]"
        >
          {{ visit.islandSnapshot.hemisphere === 'north' ? 'N' : 'S' }}
        </div>

        <div class="hidden items-center gap-1 truncate lg:flex">
          <img
            :src="`/images/icons/${visit.islandSnapshot.fruit}.svg`"
            :alt="fruitName"
            class="size-5"
          />
          <img
            v-for="icon in npcIcons"
            :key="`${icon.alt}:${icon.path}`"
            :src="icon.path"
            :alt="icon.alt"
            class="size-5"
          />
        </div>
      </div>
      <div class="flex flex-row items-center justify-end gap-2">
        <div v-if="visit.islandSnapshot.turnipPrice" class="flex items-center gap-1">
          <img src="/images/icons/turnip.svg" alt="Turnip" class="size-5" />
          <span class="text-sm font-medium text-white"
            >{{ visit.islandSnapshot.turnipPrice }} Bells</span
          >
        </div>
      </div>
    </div>
    <div class="flex items-start justify-start gap-1 truncate px-3 py-2 lg:hidden">
      <img
        :src="`/images/icons/${visit.islandSnapshot.fruit}.svg`"
        :alt="fruitName"
        class="size-5"
      />
      <img
        v-for="icon in npcIcons"
        :key="`${icon.alt}:${icon.path}`"
        :src="icon.path"
        :alt="icon.alt"
        class="size-5"
      />
    </div>
    <div
      class="grid w-full grid-cols-[1fr_9.5rem] gap-2 rounded-b-2xl px-3 pb-2 text-xs text-white"
    >
      <div class="flex flex-row items-center gap-1">
        <i-fa7-solid-clock class="size-5" />
        <span class="text-tan-400">Visited {{ formatDuration(visit.timeOnIsland ?? 0) }}</span>
        <i-fa7-solid-hourglass-start class="ml-1 size-5" />
        <span class="text-tan-400">Queued {{ formatDuration(visit.timeInQueue ?? 0) }}</span>
      </div>
      <div class="flex flex-row items-center justify-end gap-1">
        <i-fa7-solid-calendar class="size-5" />
        <span class="text-tan-400">{{ visitTime }}</span>
      </div>
    </div>
  </div>
</template>
